import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import {
  flexRender,
  type ColumnSizingState,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type SortingState,
} from '@tanstack/react-table'
// TanStack Table v9 icin v8-uyumlu API: `useReactTable`/`getCoreRowModel` artik ana
// pakette yok, `/legacy` alt yolundan gelir (bkz. paketin `useLegacyTable.d.ts`'i).
import {
  getCoreRowModel,
  useLegacyTable as useReactTable,
  type LegacyColumnDef as ColumnDef,
  type LegacyHeader,
  type LegacyRow,
} from '@tanstack/react-table/legacy'
import type { UseQueryOptions } from '@tanstack/react-query'
import {
  ArrowDownUp,
  ArrowUp,
  ChevronRight,
  Columns3,
  Funnel,
  GripVertical,
  RotateCcw,
} from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react'

import type { ColumnFilter, ColumnFilters } from '@/components/filters/column-filter'
import { ColumnFilterPopover } from '@/components/filters/ColumnFilterPopover'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Checkbox } from '@/components/ui/checkbox'
import { Pagination } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { FilterRow, type FilterRowColumn } from '@/components/data-table/FilterRow'
import { moveColumnCells } from '@/components/data-table/column-drag'
import { mergeColumnOrder } from '@/components/data-table/column-order'
import { ExportButton } from '@/components/data-table/ExportButton'
import {
  GUTTER_WIDTH,
  gridHeadCellClass as gridHeadCell,
  gridToolButtonClass,
  gridToolCountClass,
} from '@/components/data-table/grid-styles'
import type { GridCellValue } from '@/components/data-table/grid-selection'
import {
  ROW_ANIMATION_MAX_ROWS,
  rowEnterDelay,
  shouldAnimateRows,
} from '@/components/data-table/row-animation'
import { useGridSelection } from '@/components/data-table/use-grid-selection'
import { useVirtualRows } from '@/components/data-table/use-virtual-rows'
import { ROW_HEIGHT } from '@/components/data-table/virtual-blocks'
import { formatNumberPattern } from '@/lib/format'
import type { ExcelCellValue, ExcelExportColumn } from '@/lib/export-excel'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { messages } from '@/lib/messages'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'
import type { TableLayout } from '@/hooks/use-table-preferences'

/**
 * TanStack Table sarmalayıcısı — **sunucu taraflı** sayfalama/sıralama.
 * İstemcide yeniden sıralama/sayfalama yapılmaz (AGENTS.md §3, görev dosyası).
 * Kolon tanımları çağıran `features/*` tarafından verilir; bu dosya iş alanı bilmez.
 */
export interface DataTableProps<TData extends RowData> {
  /** Tablonun sabit kimliği; Excel dosya adının varsayılanı. */
  tableId: string
  // `any`: TanStack Table'ın kendi v8 örneklerindeki kalıp — kolonlar farklı `TValue`
  // taşıdığı için `unknown` çelişkiye düşer (contravariance), tek çözüm `any`'dir.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[]
  /** Sayfalı kip: ekrandaki sayfanın satırları. Sanal kipte (`virtual`) kullanılmaz. */
  data?: TData[]
  /** Sayfalı kipte toplam kayıt; sanal kipte yüklenmemişler dahil toplam satır sayısı. */
  total: number
  /** Sayfalı kip: sayfa durumu. `virtual` verilirse sayfa yoktur, ikisi de kullanılmaz. */
  pagination?: PaginationState
  onPaginationChange?: OnChangeFn<PaginationState>
  /**
   * Sanal kaydırma kipi (docs/DATA-GRID-PLAN.md Faz 5): sayfa düğmesi yok, gövde sabit yükseklikte kayar,
   * yalnız görünen satırlar çizilir, yüklenmemiş satırlar iskelet. Satırları çağıran bloklar hâlinde getirir.
   */
  virtual?: DataTableVirtual<TData>
  /**
   * Satırın kararlı kimliği (ör. view satır anahtarı). Verilirse sıralama/süzgeçte satırlar kimliğiyle
   * eşleşir: eşikli animasyonda yer değiştirenler kayar. Yoksa kimlik sıra numarasıdır.
   */
  getRowId?: (row: TData) => string
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  isLoading?: boolean
  /** Eski veri gösterilirken yenisi yükleniyor: içerik soluklaşır. */
  busy?: boolean
  isError?: boolean
  error?: unknown
  onRetry?: () => void
  emptyMessage?: string
  pageSizeOptions?: number[]
  /** Kolon sırası/genişliği/görünürlüğü — kalıcılığı çağıranın işi (bkz. `useTablePreferences`). */
  layout: TableLayout
  /** Sürükleme/boyutlandırma **bitişinde** ve görünürlük değişiminde bir kez çağrılır. */
  onLayoutChange: (layout: TableLayout) => void
  onLayoutReset: () => void
  /** Excel dosya adının temeli; verilmezse `tableId` kullanılır. */
  exportFilename?: string
  /** Alt çubukta satır sayısının yanında (ör. toplam tutar). */
  summary?: ReactNode
  /** Araç çubuğunun solunda gösterilir; ayrı başlık satırı yer kaplamasın diye. */
  title?: ReactNode
  /**
   * Excel tarzı sütun filtreleri (sunucu taraflı). Verilirse `meta.filterKind` taşıyan
   * her kolonun başlığında huni çıkar. `filterValues`: metin kolonunun değer listesi.
   */
  columnFilters?: ColumnFilters
  onColumnFiltersChange?: (filters: ColumnFilters) => void
  filterValues?: (columnId: string, search: string) => UseQueryOptions<string[]>
  /** Satıra tıklanınca (ya da odaktayken Enter) çağrılır, ör. detay paneli. */
  onRowClick?: (row: TData) => void
  /** Satıra ek sınıf (ör. üstte duran toplam satırı). */
  rowClassName?: (row: TData) => string | undefined
  /** Excel'e görünen sayfa yerine bu satırlar gider (sayfalama çağıranda yapılıyorsa). */
  exportData?: TData[]
  /**
   * Sunucu taraflı sayfalamada Excel'e bütün kayıtlar: tıklanınca aynı filtre ve
   * sıralamayla çekilir (`exportData`'dan önce gelir).
   */
  fetchExportData?: () => Promise<TData[]>
  /**
   * Altta sabit toplam satırları: sütun kimliği -> hazır metin (biçim çağıranda). Birden çok
   * satır döviz kırılımı içindir. Yükleme ve boş durumda çıkmaz; yoksa/boşsa satır yok.
   * Alt bilgideki seçim toplamından ayrıdır (o, seçili hücrelerin toplamı).
   */
  totalsRows?: GridTotalsRow[]
  /**
   * Açılan satır (yalnız sayfalı kip): satır numarası yerine ok düğmesi çıkar, açılan satırın
   * altına bu içerik tam genişlikte çizilir. İçerik (detay, geçmiş, aksiyon) çağırandan gelir.
   */
  renderSubRow?: (row: TData) => ReactNode
  /** Açık satırların kimlikleri (`getRowId`); verilmezse durum tabloda tutulur. */
  expanded?: Record<string, boolean>
  onExpandedChange?: (expanded: Record<string, boolean>) => void
}

/** Sanal kipin veri kaynağı: bileşen blok/sorgu bilmez, yalnız mutlak satır dizinini ister. */
export interface DataTableVirtual<TData> {
  /** Mutlak satır dizini -> satır; yüklenmemişse `undefined` (iskelet çizilir). */
  getRow: (index: number) => TData | undefined
  /** Yüklü satır sayısı: eşikli animasyon ve toplam satırının görünmesi buna bakar. */
  loadedCount: number
  /** Görünen (taşma payı dahil) mutlak satır aralığı değişince çağrılır; çağıran gereken blokları getirir. */
  onRangeChange: (range: { start: number; end: number }) => void
  /** Değişince gövde başa kaydırılır (sıralama/süzgeç/arama gibi satır kümesini değiştiren her şey). */
  resetKey: string
}

/** Toplam satırı: `cells` yalnız toplamı olan sütunları taşır. */
export interface GridTotalsRow {
  key: string
  cells: Record<string, string>
}

/** Başlıktaki huni + sıralama kontrolleri; `SortableHeader`'a tek nesne olarak iner. */
interface HeaderFilterControl {
  filter: ColumnFilter | undefined
  onApply: (filter: ColumnFilter | undefined) => void
  values?: (search: string) => UseQueryOptions<string[]>
  onSort: (desc: boolean) => void
  onRemoveSort: () => void
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 200]
// Backend de aynı aralığı doğrular (modules/user_preferences/schemas.py).
const MIN_COLUMN_WIDTH = 48
const MAX_COLUMN_WIDTH = 640

const clampWidth = (width: number) =>
  Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width)))

const clampSizing = (sizing: ColumnSizingState) =>
  Object.fromEntries(Object.entries(sizing).map(([id, width]) => [id, clampWidth(width)]))

const NO_FILTERS: ColumnFilters = {}
/** Sanal gövdenin en çok yüksekliği: dar ekranda bile okunur (20rem), geniş ekranda ekrana sığar. */
const VIRTUAL_MAX_HEIGHT = 'max-h-[max(20rem,min(44rem,calc(100dvh-20rem)))]'
const NO_ROWS: never[] = []
// Sanal kipte sayfa yoktur; TanStack'e tek dev sayfa gibi görünür.
const VIRTUAL_PAGINATION: PaginationState = { pageIndex: 0, pageSize: Number.MAX_SAFE_INTEGER }
/** Sanal gövdenin görünür aralığın dışında çizdiği satır sayısı (hızlı kaydırmada boşluk görünmesin). */
const VIRTUAL_OVERSCAN = 10
const ROW_ANIMATION_OVERSCAN = ROW_ANIMATION_MAX_ROWS

const columnLabel = (header: unknown, id: string) => (typeof header === 'string' ? header : id)

/** Hücrenin düz değeri: `meta.exportValue` (JSX'siz karşılık) ya da ham erişimci değeri. */
function rawCellValue<TData>(
  column: {
    columnDef: { meta?: { exportValue?: (row: TData) => string | number | null | undefined } }
    accessorFn?: (row: TData, index: number) => unknown
  },
  item: TData,
  index: number,
): unknown {
  const exportValue = column.columnDef.meta?.exportValue
  return exportValue ? (exportValue(item) ?? '') : column.accessorFn?.(item, index)
}

/** Body cells truncate to one line: the native tooltip carries the cell's full text (not the column label). */
function cellTooltip(value: unknown, enabled: boolean | undefined): string | undefined {
  if (enabled === false) return undefined
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

/** Seçim/pano değeri: sayı ham kalır (biçimsiz), boş `null`, gerisi metin. */
function toGridValue(value: unknown): GridCellValue {
  if (value === null || value === undefined) return null
  return typeof value === 'string' || typeof value === 'number' ? value : String(value)
}

export function DataTable<TData extends RowData>({
  tableId,
  columns,
  data = NO_ROWS,
  total,
  pagination: paginationProp,
  onPaginationChange,
  virtual,
  getRowId,
  sorting,
  onSortingChange,
  isLoading,
  busy,
  isError,
  error,
  onRetry,
  emptyMessage,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  layout,
  onLayoutChange,
  onLayoutReset,
  exportFilename,
  summary,
  title,
  columnFilters = NO_FILTERS,
  onColumnFiltersChange,
  filterValues,
  onRowClick,
  rowClassName,
  exportData,
  fetchExportData,
  totalsRows,
  renderSubRow,
  expanded: expandedProp,
  onExpandedChange,
}: DataTableProps<TData>) {
  const [innerExpanded, setInnerExpanded] = useState<Record<string, boolean>>({})
  const expanded = expandedProp ?? innerExpanded
  const toggleExpanded = useCallback(
    (id: string) => {
      const next = { ...expanded, [id]: !expanded[id] }
      if (!next[id]) delete next[id]
      if (onExpandedChange) onExpandedChange(next)
      else setInnerExpanded(next)
    },
    [expanded, onExpandedChange],
  )
  // Sürüklerken genişlik burada; kalıcı tercihe yalnız bırakınca yazılır.
  const [liveSizing, setLiveSizing] = useState<ColumnSizingState | null>(null)
  const liveSizingRef = useRef<ColumnSizingState | null>(null)
  // Bırakınca yeni genişlik, tercih önbelleği güncellenene kadar burada tutulur; yoksa
  // bir kare eski genişlik görünür ve kolon sıçrar.
  const [pendingSizing, setPendingSizing] = useState<ColumnSizingState | null>(null)
  const [seenSizing, setSeenSizing] = useState(layout.columnSizing)
  if (seenSizing !== layout.columnSizing) {
    setSeenSizing(layout.columnSizing)
    setPendingSizing(null)
  }
  const columnSizing = liveSizing ?? pendingSizing ?? layout.columnSizing
  const { columnVisibility } = layout
  // Kayıtlı düzenden sonra eklenmiş kolonlar sonda değil varsayılan yerinde görünsün.
  const columnOrder = useMemo(
    () =>
      mergeColumnOrder(
        layout.columnOrder,
        columns.flatMap((column) => {
          const id = column.id ?? (column as { accessorKey?: unknown }).accessorKey
          return typeof id === 'string' ? [id] : []
        }),
      ),
    [layout.columnOrder, columns],
  )

  const sensors = useSensors(
    // Mesafe eşiği: başlığa tıklamak sıralamayı değiştirir, sürüklemeye dönüşmez.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const isVirtual = virtual !== undefined
  const pagination = paginationProp ?? VIRTUAL_PAGINATION
  const pageCount = total > 0 ? Math.ceil(total / pagination.pageSize) : 0

  // Filtre satırı: Filtre düğmesi açar/kapar; herhangi bir filtre varken açık başlar.
  const [filterRowOpen, setFilterRowOpen] = useState(() => Object.keys(columnFilters).length > 0)

  // Hücre aralığı seçimi + Ctrl+C (docs/DATA-GRID-PLAN.md Faz 2). Görünüm hücre özniteliklerinde.
  const gridRef = useRef<HTMLDivElement>(null)

  // Açılan satır içeriği tablonun tamamı kadar değil, görünen alan kadar geniş (yatay kaydırmada
  // sola yapışık kalır).
  const [subRowWidth, setSubRowWidth] = useState<number>()
  useEffect(() => {
    const element = gridRef.current
    if (!renderSubRow || !element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setSubRowWidth(element.clientWidth))
    observer.observe(element)
    return () => observer.disconnect()
  }, [renderSubRow])

  // Sanal kip (Faz 5): yalnız görünen satırlar çizilir. Başlık (+ filtre satırı) ve toplam satırı sabit
  // olduğundan kaydırma kapsayıcısının başındaki/sonundaki bu yükseklikler sanallaştırıcıya bildirilir.
  const reducedMotion = useReducedMotion() ?? false
  const loadedRows = virtual ? virtual.loadedCount : data.length
  const animateRows = shouldAnimateRows(loadedRows, reducedMotion)
  const headHeight = ROW_HEIGHT * (filterRowOpen && onColumnFiltersChange ? 2 : 1)
  const totalsHeight = totalsRows && loadedRows > 0 ? totalsRows.length * ROW_HEIGHT : 0
  const {
    items: virtualItems,
    totalSize,
    scrollToRow,
  } = useVirtualRows({
    count: virtual ? total : 0,
    scrollRef: gridRef,
    headHeight,
    footHeight: totalsHeight,
    // Animasyon eşiğinin altında (< 100 satır) hepsi çizilir: kaydırırken satırlar yeniden girmez.
    overscan: animateRows ? ROW_ANIMATION_OVERSCAN : VIRTUAL_OVERSCAN,
  })
  const virtualGetRow = virtual?.getRow
  // Yüklü olanlar tabloya girer; yüklenmemiş konumlar iskelet olarak ayrıca çizilir.
  const windowRows = useMemo(() => {
    const indexes: number[] = []
    const items: TData[] = []
    if (virtualGetRow) {
      for (const { index } of virtualItems) {
        const item = virtualGetRow(index)
        if (item !== undefined) {
          indexes.push(index)
          items.push(item)
        }
      }
    }
    return { indexes, items }
  }, [virtualItems, virtualGetRow])

  const table = useReactTable({
    data: virtual ? windowRows.items : data,
    columns,
    getRowId: virtual
      ? (row, index) => getRowId?.(row) ?? String(windowRows.indexes[index] ?? index)
      : getRowId && ((row) => getRowId(row)),
    state: { sorting, pagination, columnVisibility, columnOrder, columnSizing },
    defaultColumn: { minSize: MIN_COLUMN_WIDTH, maxSize: MAX_COLUMN_WIDTH },
    columnResizeMode: 'onChange',
    onColumnSizingChange: (updater) => {
      const prev = liveSizingRef.current ?? columnSizing
      const next = typeof updater === 'function' ? updater(prev) : updater
      liveSizingRef.current = next
      setLiveSizing(next)
    },
    manualSorting: true,
    manualPagination: true,
    pageCount,
    onSortingChange,
    onPaginationChange,
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnVisibility) : updater
      onLayoutChange({ ...layout, columnVisibility: next })
    },
    getCoreRowModel: getCoreRowModel(),
  })

  const bodyRows = table.getRowModel().rows
  const visibleColumns = table.getVisibleLeafColumns()
  const columnKey = visibleColumns.map((column) => column.id).join()
  // Sanal kipte dizinler mutlak ve sınır toplam satır sayısıdır (yüklenmemişler dahil); yüklü olmayan hücre boş.
  const getCellValue = useCallback(
    (row: number, col: number) => {
      const item = virtualGetRow ? virtualGetRow(row) : bodyRows[row]?.original
      const column = visibleColumns[col]
      return item === undefined || !column ? null : toGridValue(rawCellValue(column, item, row))
    },
    [bodyRows, visibleColumns, virtualGetRow],
  )
  const selection = useGridSelection({
    containerRef: gridRef,
    bounds: { rows: isVirtual ? total : bodyRows.length, cols: visibleColumns.length },
    resetKey: `${virtual ? virtual.resetKey : `${pagination.pageIndex}:${pagination.pageSize}`}:${JSON.stringify(sorting)}:${JSON.stringify(columnFilters)}:${columnKey}`,
    getValue: getCellValue,
    rowsVersion: bodyRows,
    scrollToRow: isVirtual ? scrollToRow : undefined,
  })

  // Satır kümesi değişince (sıralama/süzgeç) başa dön; yoksa görünen aralığı çağırana bildir (o, blokları getirir).
  const virtualReset = virtual?.resetKey
  const onRangeChange = virtual?.onRangeChange
  const firstVisible = virtualItems[0]?.index
  const lastVisible = virtualItems[virtualItems.length - 1]?.index
  const appliedReset = useRef(virtualReset)
  useEffect(() => {
    if (!onRangeChange) return
    if (appliedReset.current !== virtualReset) {
      appliedReset.current = virtualReset
      if (gridRef.current) gridRef.current.scrollTop = 0
      // Eski kaydırma konumunun blokları istenmesin: yalnız baş; gerçek aralık kaydırma olayıyla gelir.
      onRangeChange({ start: 0, end: 0 })
      return
    }
    if (firstVisible !== undefined && lastVisible !== undefined) {
      onRangeChange({ start: firstVisible, end: lastVisible })
    }
  }, [virtualReset, onRangeChange, firstVisible, lastVisible])
  // `TableRows` memo'lu kalsın diye kararlı kimlik; çağıranın en güncel `onRowClick`ini çağırır.
  const onRowClickRef = useRef(onRowClick)
  useEffect(() => {
    onRowClickRef.current = onRowClick
  })
  const { shouldBlockRowClick } = selection
  const handleRowClick = useCallback(
    (row: TData) => {
      if (!shouldBlockRowClick()) onRowClickRef.current?.(row)
    },
    [shouldBlockRowClick],
  )

  /** Power Query gibi: yeni sütun sıralamanın sonuna eklenir, var olanın yalnızca yönü değişir. */
  const sortColumn = (id: string, desc: boolean) =>
    onSortingChange(
      sorting.some((item) => item.id === id)
        ? sorting.map((item) => (item.id === id ? { id, desc } : item))
        : [...sorting, { id, desc }],
    )
  const unsortColumn = (id: string) => onSortingChange(sorting.filter((item) => item.id !== id))

  const setColumnFilter = (id: string, filter: ColumnFilter | undefined) => {
    if (!onColumnFiltersChange) return
    const rest = Object.fromEntries(Object.entries(columnFilters).filter(([key]) => key !== id))
    onColumnFiltersChange(filter ? { ...rest, [id]: filter } : rest)
  }
  const filterControl = (columnId: string): HeaderFilterControl | undefined => {
    if (!onColumnFiltersChange || !table.getColumn(columnId)?.columnDef.meta?.filterKind) {
      return undefined
    }
    return {
      filter: columnFilters[columnId],
      onApply: (filter) => setColumnFilter(columnId, filter),
      values: filterValues && ((search) => filterValues(columnId, search)),
      onSort: (desc) => sortColumn(columnId, desc),
      onRemoveSort: () => unsortColumn(columnId),
    }
  }

  const labelOf = (id: string | number) => {
    const column = table.getColumn(String(id))
    return column ? columnLabel(column.columnDef.header, column.id) : String(id)
  }
  const announcements: Announcements = {
    onDragStart: ({ active }) => messages.common.columnDrag.start(labelOf(active.id)),
    onDragOver: ({ active, over }) =>
      over ? messages.common.columnDrag.over(labelOf(active.id), labelOf(over.id)) : undefined,
    onDragEnd: ({ active }) => messages.common.columnDrag.end(labelOf(active.id)),
    onDragCancel: ({ active }) => messages.common.columnDrag.cancel(labelOf(active.id)),
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    // Gizli kolonlar da sırada kalır ki tekrar açıldıklarında yerleri korunsun.
    const ids = table.getAllLeafColumns().map((column) => column.id)
    const next = arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)))
    onLayoutChange({ ...layout, columnOrder: next })
  }

  const commitSizing = (sizing: ColumnSizingState) => {
    const next = clampSizing(sizing)
    setPendingSizing(next)
    onLayoutChange({ ...layout, columnSizing: next })
  }

  const startResize = (header: LegacyHeader<TData, unknown>, event: unknown) => {
    header.getResizeHandler()(event)
    // İmleç tutamaktan taşınca yanıp sönmesin.
    document.body.style.cursor = 'col-resize'
    // TanStack bırakmayı `document`'ta dinler; `window` ondan sonra gelir, son genişlik hazırdır.
    const onEnd = () => {
      window.removeEventListener('mouseup', onEnd)
      window.removeEventListener('touchend', onEnd)
      document.body.style.cursor = ''
      const sizing = liveSizingRef.current
      liveSizingRef.current = null
      setLiveSizing(null)
      if (sizing) commitSizing(sizing)
    }
    window.addEventListener('mouseup', onEnd)
    window.addEventListener('touchend', onEnd)
  }

  const resetSize = (columnId: string) =>
    commitSizing(Object.fromEntries(Object.entries(columnSizing).filter(([id]) => id !== columnId)))

  const rowOffset = pagination.pageIndex * pagination.pageSize
  // Gövdenin çizilecek konumları: sayfalıda sayfanın satırları, sanalda görünen aralık (yüklenmemiş = iskelet).
  const slots = useMemo<RowSlot<TData>[]>(() => {
    if (!virtualGetRow) {
      return bodyRows.map((row, index) => ({
        key: row.id,
        dataRow: index,
        label: rowOffset + index + 1,
        row,
      }))
    }
    const byIndex = new Map(windowRows.indexes.map((index, i) => [index, bodyRows[i]]))
    return virtualItems.map(({ index }) => ({
      key: byIndex.get(index)?.id ?? `skeleton-${index}`,
      dataRow: index,
      label: index + 1,
      row: byIndex.get(index) ?? null,
    }))
  }, [bodyRows, rowOffset, virtualGetRow, virtualItems, windowRows.indexes])
  const lastItem = virtualItems[virtualItems.length - 1]
  const padTop = virtualItems[0] ? virtualItems[0].start - headHeight : 0
  const padBottom = lastItem ? totalSize - (lastItem.end - headHeight) : 0
  const hasRows = virtual ? virtual.loadedCount > 0 : bodyRows.length > 0

  if (isError) return <ErrorState error={error} onRetry={onRetry} />

  const exportColumns = table
    .getAllLeafColumns()
    .filter((column) => column.getIsVisible() && column.columnDef.meta?.exportable !== false)
  const exportColumnDefs: ExcelExportColumn[] = exportColumns.map((column) => ({
    header: columnLabel(column.columnDef.header, column.id),
    numberFormat: column.columnDef.meta?.exportNumberFormat,
  }))
  const toExportRows = (items: TData[]): ExcelCellValue[][] =>
    items.map((item, index) =>
      exportColumns.map((column) => {
        const value = rawCellValue(column, item, index)
        if (value === null || value === undefined) return ''
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return value
        }
        return String(value)
      }),
    )
  // `exportData` (istemcide sayfalanan büyük liste) yalnız tıklanınca dönüştürülür: her
  // çizimde on binlerce satırı hazırlamak sıralama/süzmede ekranı saniyelerce donduruyordu.
  const exportRows =
    fetchExportData || exportData
      ? []
      : toExportRows(table.getRowModel().rows.map((row) => row.original))
  const getExportRows = fetchExportData
    ? async () => toExportRows(await fetchExportData())
    : exportData && (async () => toExportRows(exportData))

  const activeFilterCount = Object.keys(columnFilters).length
  const filterRowColumns: FilterRowColumn[] = visibleColumns.map((column) => ({
    id: column.id,
    label: columnLabel(column.columnDef.header, column.id),
    kind: column.columnDef.meta?.filterKind,
  }))
  const visibleColumnCount = visibleColumns.length
  return (
    <div className="rounded-panel border-border bg-surface text-foreground flex min-w-0 flex-col overflow-hidden border text-sm">
      {/* Araç çubuğu (uiarc veri ızgarası): 52px, altında çizgi; düğmeler sağda. */}
      <div className="border-border flex h-13 items-center gap-1 border-b pr-2 pl-2.5">
        {title && <h2 className="shrink-0 pr-2 pl-1.5 text-sm font-medium">{title}</h2>}
        <span className="min-w-1 flex-1" />
        {onColumnFiltersChange && (
          <>
            <button
              type="button"
              aria-pressed={filterRowOpen}
              onClick={() => setFilterRowOpen((open) => !open)}
              className={gridToolButtonClass}
            >
              <Funnel />
              <span>{messages.common.filter}</span>
              {activeFilterCount > 0 && (
                <span className={gridToolCountClass}>{activeFilterCount}</span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={() => onColumnFiltersChange({})}
                className={gridToolButtonClass}
              >
                {messages.grid.clearFilters}
              </button>
            )}
          </>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger render={<button type="button" className={gridToolButtonClass} />}>
            <Columns3 />
            {messages.common.columns}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
            {table
              .getAllLeafColumns()
              .filter((column) => column.getCanHide())
              .map((column) => (
                <DropdownMenuItem
                  key={column.id}
                  closeOnClick={false}
                  onClick={() => column.toggleVisibility()}
                  className="justify-between"
                >
                  <span className="truncate">
                    {columnLabel(column.columnDef.header, column.id)}
                  </span>
                  <Checkbox checked={column.getIsVisible()} />
                </DropdownMenuItem>
              ))}
            <DropdownMenuSeparator />
            {sorting.length > 0 && (
              <DropdownMenuItem onClick={() => onSortingChange([])}>
                <ArrowDownUp />
                {messages.filters.column.clearSort}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onLayoutReset}>
              <RotateCcw />
              {messages.common.resetColumns}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ExportButton
          appearance="toolbar"
          filename={exportFilename ?? tableId}
          columns={exportColumnDefs}
          rows={exportRows}
          getRows={getExportRows}
          disabled={exportData ? exportData.length === 0 : Boolean(fetchExportData) && total === 0}
        />
      </div>

      <div
        ref={gridRef}
        // Odaklanabilir: Ctrl+C/ok tuşları buradan geçer; hücre seçimi `useGridSelection`da.
        tabIndex={0}
        aria-busy={busy || undefined}
        // Sabit başlık ve toplam satırı klavye/sürükleme kaydırmasında hedefi örtmesin.
        style={{ scrollPaddingTop: headHeight, scrollPaddingBottom: totalsHeight }}
        {...selection.handlers}
        className={cn(
          // Yalnız yatayda `contain` (geri-git kaydırma jesti tetiklenmesin). Dikeyde zincirleme açık:
          // sayfalı tablonun kendi dikey kaydırması yok, `contain` iken satır üstünde tekerlek sayfayı
          // da kaydırmıyordu; sanal tabloda da uca gelince sayfa kaymaya devam eder.
          'overflow-auto overscroll-x-contain transition-opacity duration-(--duration-standard) focus-visible:outline-offset-[-2px]',
          // Sanal kip sabit yükseklikte kayar (küçük tabloda içeriğe kadar kısalır); başlık ve toplam yapışık.
          isVirtual && VIRTUAL_MAX_HEIGHT,
          busy && 'opacity-60',
        )}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          accessibility={{
            announcements,
            screenReaderInstructions: { draggable: messages.common.columnDrag.instructions },
          }}
        >
          <table
            className="table-fixed border-separate border-spacing-0 text-xs"
            style={{ width: table.getCenterTotalSize() + GUTTER_WIDTH }}
          >
            <thead className="sticky top-0 z-[5]">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <th
                    scope="col"
                    className={cn(
                      gridHeadCell,
                      'text-text-muted sticky left-0 z-[3] px-0 text-center text-xs font-normal',
                      'shadow-[inset_-1px_0_0_var(--border)]',
                    )}
                    style={{ width: GUTTER_WIDTH }}
                    title={messages.common.rowNumber}
                  >
                    <span aria-hidden="true">#</span>
                    <span className="sr-only">{messages.common.rowNumber}</span>
                  </th>
                  <SortableContext
                    items={headerGroup.headers.map((header) => header.column.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <SortableHeader
                        key={header.id}
                        header={header}
                        onResizeStart={startResize}
                        onResetSize={resetSize}
                        showSortIndex={sorting.length > 1}
                        filterControl={filterControl(header.column.id)}
                      />
                    ))}
                  </SortableContext>
                </tr>
              ))}
              {filterRowOpen && onColumnFiltersChange && (
                <FilterRow
                  columns={filterRowColumns}
                  filters={columnFilters}
                  onChange={setColumnFilter}
                />
              )}
            </thead>
            <TableRows
              slots={slots}
              columnKey={columnKey}
              colSpan={visibleColumnCount + 1}
              padTop={padTop}
              padBottom={padBottom}
              animate={animateRows}
              isLoading={isLoading}
              emptyMessage={emptyMessage}
              onRowClick={onRowClick ? handleRowClick : undefined}
              rowClassName={rowClassName}
              renderSubRow={isVirtual ? undefined : renderSubRow}
              expanded={expanded}
              onToggleExpanded={toggleExpanded}
              subRowWidth={subRowWidth}
            />
            {totalsRows && totalsRows.length > 0 && !isLoading && hasRows && (
              <TotalsFooter rows={totalsRows} columns={visibleColumns} />
            )}
          </table>
        </DndContext>
      </div>

      {/* Alt bilgi (uiarc durum çubuğu + pagination): sol kayıt sayısı ve özet, sağ sayfalama. */}
      <div className="border-border text-text-secondary flex min-h-11 flex-wrap items-center justify-between gap-x-5 gap-y-1 border-t px-4 py-2 text-xs tabular-nums">
        <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
          <span>{messages.pagination.totalRows(total)}</span>
          {summary}
          <span aria-live="polite" className="contents">
            {selection.summary && (
              <span>
                {messages.grid.cellsSelected(selection.summary.cells)}
                {selection.summary.sum !== null &&
                  ` · ${messages.grid.selectionSum}: ${formatNumberPattern(selection.summary.sum, '#,##0.###')}`}
              </span>
            )}
            {selection.copied && <span className="text-foreground">{messages.grid.copied}</span>}
          </span>
        </span>
        {!isVirtual && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {pageSizeOptions.length > 1 && (
              <div className="flex items-center gap-2">
                <span aria-hidden="true">{messages.pagination.rowsPerPage}</span>
                <OptionSelect
                  size="sm"
                  aria-label={messages.pagination.rowsPerPage}
                  className="w-20"
                  value={String(pagination.pageSize)}
                  options={pageSizeOptions.map((size) => ({
                    value: String(size),
                    label: String(size),
                  }))}
                  onValueChange={(next) => {
                    onPaginationChange?.({ pageIndex: 0, pageSize: Number(next) })
                  }}
                />
              </div>
            )}
            <span className="text-text-muted">
              {messages.pagination.pageOf(pagination.pageIndex + 1, Math.max(pageCount, 1))}
            </span>
            <Pagination
              page={pagination.pageIndex + 1}
              pageCount={pageCount}
              onPageChange={(page) => table.setPageIndex(page - 1)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

/** Gövde hücresinin ortak çizgileri (uiarc `cell`): alt ve sağda `border-subtle`. */
const gridBodyCell =
  'border-border-subtle h-10 border-b bg-inherit align-middle shadow-[inset_-1px_0_0_var(--border-subtle)]'

/** Gövdenin bir konumu: gerçek satır ya da (`row: null`) yüklenmemiş satırın iskeleti. */
interface RowSlot<TData extends RowData> {
  key: string
  /** Hücrelerin `data-row` değeri: sayfalıda sayfa içi, sanalda mutlak satır dizini. */
  dataRow: number
  /** Satır numarası sütununda yazan (1 tabanlı, mutlak). */
  label: number
  row: LegacyRow<TData> | null
}

interface TableRowsProps<TData extends RowData> {
  slots: RowSlot<TData>[]
  /** Görünür kolon sırası; değişince satırlar yeniden çizilir. */
  columnKey: string
  /** Satır numarası sütunu dahil. */
  colSpan: number
  /** Sanal kip: çizilen aralığın önündeki/ardındaki çizilmeyen satırların yüksekliği (px). */
  padTop: number
  padBottom: number
  /** Eşikli satır animasyonu (kayma + kademeli giriş), bkz. `row-animation.ts`. */
  animate: boolean
  isLoading?: boolean
  emptyMessage?: string
  onRowClick?: (row: TData) => void
  rowClassName?: (row: TData) => string | undefined
  renderSubRow?: (row: TData) => ReactNode
  expanded: Record<string, boolean>
  onToggleExpanded: (id: string) => void
  /** Açılan satır içeriğinin genişliği: yatay kaydırmada görünür alanda kalsın (px). */
  subRowWidth: number | undefined
}

/** Satır kabuğu: animasyon açıkken `motion.tr` (yer değiştirenler kayar, yeniler kademeli girer), değilse düz `tr`. */
function RowShell({
  animate,
  index,
  count,
  children,
  ...props
}: {
  animate: boolean
  index: number
  count: number
  className: string
  tabIndex?: number
  onClick?: () => void
  onKeyDown?: (event: KeyboardEvent) => void
  children: ReactNode
}) {
  if (!animate) return <tr {...props}>{children}</tr>
  return (
    <motion.tr
      {...props}
      data-animated=""
      layout="position"
      // Konum ölçümü yalnız sıra değişince: satır açma/kapama alttaki satırları kaydırmasın, her
      // yeniden çizimde bütün satırların ölçülmesi (zorunlu reflow) da kalksın.
      layoutDependency={index}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        layout: motionTokens.spring.smooth,
        default: {
          duration: motionTokens.duration.standard,
          ease: motionTokens.ease.enter,
          delay: rowEnterDelay(index, count),
        },
      }}
    >
      {children}
    </motion.tr>
  )
}

const spacerRow = (height: number, colSpan: number) =>
  height > 0 && (
    <tr aria-hidden="true" style={{ height }}>
      <td colSpan={colSpan} className="p-0" style={{ height }} />
    </tr>
  )

/**
 * Genişlik değişince yeniden çizilmez: `table-fixed` genişliği başlıktan alır. Sürüklerken
 * her harekette tüm satırları çizmek imleci geride bırakıp titretiyordu.
 */
const TableRows = memo(function TableRows<TData extends RowData>({
  slots,
  colSpan,
  padTop,
  padBottom,
  animate,
  isLoading,
  emptyMessage,
  onRowClick,
  rowClassName,
  renderSubRow,
  expanded,
  onToggleExpanded,
  subRowWidth,
}: TableRowsProps<TData>) {
  const columnCount = colSpan - 1
  return (
    <tbody className="[&_tr:last-child>td]:border-b-0">
      {isLoading ? (
        <tr>
          <td colSpan={colSpan}>
            <Loading rows={8} />
          </td>
        </tr>
      ) : slots.length === 0 ? (
        <tr>
          <td colSpan={colSpan}>
            <Empty hint={emptyMessage ?? messages.states.emptyHint} />
          </td>
        </tr>
      ) : (
        <>
          {spacerRow(padTop, colSpan)}
          {slots.map(({ key, dataRow, label, row }, index) =>
            row === null ? (
              <SkeletonRow
                key={key}
                dataRow={dataRow}
                label={label}
                columnCount={columnCount}
                index={index}
              />
            ) : (
              <Fragment key={key}>
                <RowShell
                  animate={animate}
                  index={index}
                  count={slots.length}
                  // Hücreler `bg-inherit`: satır zemini (ve çağıranın `rowClassName`ı) hücrelere iner.
                  className={cn(
                    'bg-surface ease-standard transition-colors duration-(--duration-standard) hover:bg-[color-mix(in_oklab,var(--foreground)_3%,var(--surface))] motion-reduce:transition-none',
                    onRowClick && 'cursor-pointer',
                    rowClassName?.(row.original),
                  )}
                  {...(onRowClick && {
                    tabIndex: 0,
                    onClick: () => onRowClick(row.original),
                    onKeyDown: (event: KeyboardEvent) => {
                      if (event.key === 'Enter') onRowClick(row.original)
                    },
                  })}
                >
                  <td
                    className={cn(
                      gridBodyCell,
                      'text-text-muted sticky left-0 z-[3] px-0 text-center text-xs tabular-nums',
                      'shadow-[inset_-1px_0_0_var(--border)]',
                    )}
                    aria-hidden={renderSubRow ? undefined : true}
                  >
                    {renderSubRow ? (
                      <button
                        type="button"
                        aria-expanded={Boolean(expanded[row.id])}
                        aria-label={
                          expanded[row.id] ? messages.grid.collapseRow : messages.grid.expandRow
                        }
                        onClick={(event) => {
                          event.stopPropagation()
                          onToggleExpanded(row.id)
                        }}
                        className="text-text-muted hover:text-foreground rounded-control inline-flex size-7 items-center justify-center transition-colors focus-visible:outline-2"
                      >
                        <ChevronRight
                          className={cn(
                            'ease-standard size-4 transition-transform duration-(--duration-standard) motion-reduce:transition-none',
                            expanded[row.id] && 'rotate-90',
                          )}
                        />
                      </button>
                    ) : (
                      label
                    )}
                  </td>
                  {row.getVisibleCells().map((cell, colIndex) => (
                    <td
                      key={cell.id}
                      data-row={dataRow}
                      data-col={colIndex}
                      title={cellTooltip(cell.getValue(), cell.column.columnDef.meta?.cellTooltip)}
                      className={cn(
                        gridBodyCell,
                        'truncate px-3 tabular-nums select-none',
                        cell.column.columnDef.meta?.numeric && 'text-right',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </RowShell>
                {renderSubRow && expanded[row.id] && (
                  <tr data-sub-row="">
                    <td
                      colSpan={colSpan}
                      className="border-border-subtle bg-surface-muted/40 border-b p-0"
                    >
                      <div className="sticky left-0" style={{ width: subRowWidth }}>
                        {renderSubRow(row.original)}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ),
          )}
          {spacerRow(padBottom, colSpan)}
        </>
      )}
    </tbody>
  )
}) as <TData extends RowData>(props: TableRowsProps<TData>) => ReactNode

/** Henüz yüklenmemiş satır: gerçek satırla aynı ölçüler ve hücre öznitelikleri (seçim/kaydırma sürsün). */
function SkeletonRow({
  dataRow,
  label,
  columnCount,
  index,
}: {
  dataRow: number
  label: number
  columnCount: number
  index: number
}) {
  return (
    <tr className="bg-surface" aria-hidden="true" data-skeleton-row="">
      <td
        className={cn(
          gridBodyCell,
          'text-text-muted sticky left-0 z-[3] px-0 text-center text-xs tabular-nums',
          'shadow-[inset_-1px_0_0_var(--border)]',
        )}
      >
        {label}
      </td>
      {Array.from({ length: columnCount }, (_, colIndex) => (
        <td
          key={colIndex}
          data-row={dataRow}
          data-col={colIndex}
          className={cn(gridBodyCell, 'px-3 select-none')}
        >
          <Skeleton
            className="h-3 w-3/5"
            style={{ '--index': (index + colIndex) % 8 } as CSSProperties}
          />
        </td>
      ))}
    </tr>
  )
}

/** Toplam satırı hücre yüksekliği (`h-10`); birden çok satır alttan bu adımla yapışır. */
const TOTALS_ROW_HEIGHT = 40

/**
 * Alta yapışık toplam satırları (`<tfoot>`; hücreler `sticky bottom`, gutter ayrıca sol). Toplamı
 * olmayan ilk sütun yoksa etiket gutter'da `Σ`; birinci sütunun toplamı yoksa etiket orada yazılır.
 */
function TotalsFooter({
  rows,
  columns,
}: {
  rows: GridTotalsRow[]
  columns: { id: string; columnDef: { meta?: { numeric?: boolean } } }[]
}) {
  const labelColumn = columns[0]
  const showLabel = labelColumn !== undefined && rows.every((row) => !(labelColumn.id in row.cells))
  return (
    <tfoot>
      {rows.map((row, index) => {
        const bottom = (rows.length - 1 - index) * TOTALS_ROW_HEIGHT
        const cell =
          'border-border bg-surface-muted text-foreground sticky z-[2] h-10 border-t align-middle font-medium shadow-[inset_-1px_0_0_var(--border-subtle)]'
        return (
          <tr key={row.key} data-totals-row="">
            <td
              className={cn(
                cell,
                'text-text-muted left-0 z-[4] px-0 text-center text-xs shadow-[inset_-1px_0_0_var(--border)]',
              )}
              style={{ bottom }}
            >
              <span aria-hidden="true">Σ</span>
              <span className="sr-only">{messages.grid.totalRow}</span>
            </td>
            {columns.map((column, colIndex) => (
              <td
                key={column.id}
                data-totals-col={column.id}
                className={cn(
                  cell,
                  'truncate px-3 tabular-nums',
                  column.columnDef.meta?.numeric && 'text-right',
                )}
                style={{ bottom }}
              >
                {row.cells[column.id] ??
                  (showLabel && colIndex === 0 && index === 0 ? messages.grid.totalRow : null)}
              </td>
            ))}
          </tr>
        )
      })}
    </tfoot>
  )
}

interface SortableHeaderProps<TData extends RowData> {
  header: LegacyHeader<TData, unknown>
  onResizeStart: (header: LegacyHeader<TData, unknown>, event: unknown) => void
  onResetSize: (columnId: string) => void
  showSortIndex: boolean
  filterControl?: HeaderFilterControl
}

/** Tutamaçtan sürüklenir (sıralama); sağ kenardaki çizgi genişliği ayarlar. */
function SortableHeader<TData extends RowData>({
  header,
  onResizeStart,
  onResetSize,
  showSortIndex,
  filterControl,
}: SortableHeaderProps<TData>) {
  const {
    attributes,
    listeners,
    node,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: header.column.id,
    attributes: { roleDescription: messages.common.columnDrag.role },
  })
  const canSort = header.column.getCanSort()
  const sortDirection = header.column.getIsSorted()
  const sortIndex = header.column.getSortIndex()
  const filterKind = header.column.columnDef.meta?.filterKind
  const numeric = header.column.columnDef.meta?.numeric
  const label = columnLabel(header.column.columnDef.header, header.column.id)

  // Hücreler başlıkla birlikte kayar (bkz. `moveColumnCells`); satır numarası sütunu ilk hücre.
  const x = transform?.x
  useLayoutEffect(() => {
    const table = node.current?.closest('table')
    if (table) moveColumnCells(table, header.index + 1, { x, transition, dragging: isDragging })
  }, [node, header.index, x, transition, isDragging])

  const filterButton = filterControl && filterKind && (
    <ColumnFilterPopover
      label={label}
      kind={filterKind}
      filter={filterControl.filter}
      onApply={filterControl.onApply}
      values={filterControl.values}
      sort={
        canSort
          ? {
              direction: sortDirection,
              onSort: filterControl.onSort,
              onRemove: filterControl.onRemoveSort,
            }
          : undefined
      }
    />
  )
  const filterActive = filterControl?.filter !== undefined

  return (
    <th
      ref={setNodeRef}
      scope="col"
      // Ad kısaltılırsa tam adı ipucunda.
      title={header.column.columnDef.meta?.title ?? label}
      aria-sort={
        sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : undefined
      }
      className={cn(gridHeadCell, 'group/header relative', isDragging && 'bg-surface-muted z-20')}
      style={{
        width: header.getSize(),
        transform: x ? `translate3d(${x}px, 0, 0)` : undefined,
        transition,
      }}
    >
      {/* Ad genişliğin tamamını alır. Sağdaki şeritte menü düğmesi en sağda sabit: filtre etkinse hep
          görünür, değilse üzerine gelince/odakta/açıkken; taşıma tutamacı yalnız üzerine gelince, menünün
          solunda. Şerit tıklamayı yutmaz, yalnız görünen düğmeler yakalar: altında kalan ada tıklamak
          yine sıralar. Dokunmatikte üzerine gelme yok: araçlar hep görünür ve akışta. */}
      <div className="flex h-full items-stretch">
        {header.isPlaceholder ? null : (
          <button
            type="button"
            disabled={!canSort}
            // Tık: yalnız bu sütuna göre; Shift+tık: sonraki öncelik (TanStack varsayılanı).
            onClick={header.column.getToggleSortingHandler()}
            data-sorted={sortDirection || undefined}
            className={cn(
              'group/sort text-text-secondary ease-standard flex min-w-0 grow cursor-pointer items-center gap-1 pr-1 pl-3 text-left font-medium transition-colors duration-(--duration-fast) select-none',
              'hover:enabled:text-foreground data-sorted:text-foreground disabled:cursor-default motion-reduce:transition-none',
              numeric && 'flex-row-reverse justify-start text-right',
              // Etkin huni adın üstüne binmesin.
              filterActive && 'pr-7 pointer-coarse:pr-1',
            )}
          >
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
              {flexRender(header.column.columnDef.header, header.getContext())}
            </span>
            {canSort && (
              <span
                aria-hidden="true"
                data-dir={sortDirection || undefined}
                className={cn(
                  'text-foreground inline-flex shrink-0 scale-[0.8] items-center gap-px opacity-0',
                  'ease-standard transition-[opacity,transform] duration-(--duration-fast)',
                  'data-dir:scale-100 data-dir:opacity-100',
                  'group-hover/sort:[&:not([data-dir])]:scale-100 group-hover/sort:[&:not([data-dir])]:opacity-35',
                  'motion-reduce:transition-none',
                )}
              >
                <ArrowUp
                  className={cn(
                    'ease-spring size-3.5 transition-transform duration-(--duration-spring) motion-reduce:transition-none',
                    sortDirection === 'desc' && 'rotate-180',
                  )}
                  strokeWidth={1.75}
                />
              </span>
            )}
            {showSortIndex && sortIndex >= 0 && (
              <span
                title={messages.filters.column.sortPriority(sortIndex + 1)}
                className="text-text-secondary min-w-[1ch] text-xs leading-none tabular-nums"
              >
                {sortIndex + 1}
              </span>
            )}
          </button>
        )}
        <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center pointer-coarse:static">
          <span
            className={cn(
              'bg-surface hidden items-center pl-0.5',
              'group-focus-within/header:pointer-events-auto group-focus-within/header:flex',
              'group-hover/header:pointer-events-auto group-hover/header:flex',
              'pointer-coarse:pointer-events-auto pointer-coarse:flex',
            )}
          >
            <button
              ref={setActivatorNodeRef}
              type="button"
              className="text-text-muted hover:bg-surface-muted hover:text-foreground flex size-6 shrink-0 cursor-grab touch-none items-center justify-center rounded-lg transition-colors"
              aria-label={messages.common.columnDrag.handle(label)}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="size-3.5" />
            </button>
          </span>
          {filterButton && (
            <span
              className={cn(
                'bg-surface ease-standard flex transition-opacity duration-(--duration-fast)',
                filterActive
                  ? 'pointer-events-auto'
                  : [
                      'opacity-0',
                      'group-focus-within/header:pointer-events-auto group-focus-within/header:opacity-100',
                      'group-hover/header:pointer-events-auto group-hover/header:opacity-100',
                      'has-[[data-popup-open]]:pointer-events-auto has-[[data-popup-open]]:opacity-100',
                      'pointer-coarse:pointer-events-auto pointer-coarse:opacity-100',
                    ],
              )}
            >
              {filterButton}
            </span>
          )}
        </div>
      </div>
      {header.column.getCanResize() && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={messages.common.resizeColumn}
          title={messages.common.resizeColumnHint}
          onMouseDown={(event) => onResizeStart(header, event)}
          onTouchStart={(event) => onResizeStart(header, event)}
          onDoubleClick={() => onResetSize(header.column.id)}
          data-active={header.column.getIsResizing() || undefined}
          className={cn(
            'group/resizer absolute top-0 -right-1.5 z-10 flex h-full w-3 cursor-col-resize touch-none justify-center select-none',
            // uiarc `resizer`: 2px çubuk, üzerine gelince yarı görünür, sürüklerken tam.
            'before:bg-foreground before:absolute before:inset-y-[9px] before:left-[5px] before:w-0.5 before:scale-y-50 before:rounded-[1px] before:opacity-0',
            'before:ease-standard before:transition-[opacity,transform] before:duration-(--duration-fast)',
            'hover:before:scale-y-100 hover:before:opacity-45 data-active:before:scale-y-100 data-active:before:opacity-100',
            'motion-reduce:before:transition-none',
          )}
        />
      )}
    </th>
  )
}
